import React from 'react';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { Button, Icon, Tooltip, CreateBy } from 'components';
import { ComponentBaseProps } from 'types';
import { CADComparisonHeight, NavCubWidth } from 'styles/constants';
import { Link } from 'core';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { CadFileIcon, CloudIcon, NavHomeIcon } from 'icons';
import { formatDate } from 'utils';
import { frameStore } from 'apps/components/Frame/appManager';
import { themes } from 'styles/themes/wikifactoryNew';
import { SharedFileRevision, User } from 'generated/typescript';
import useOutisdeClick from '../headerDropdown/useOutsideClick';
import {
  DropdownContentWrapper,
  DropdownInitialWrapper,
  RowTitleWrapper,
} from '../headerDropdown/dropdown.styled';

const isDesktopMode = isInDesktopMode();

type ConflictButtonProps = {
  text?: string;
  icon?: React.ReactNode;
  isSelected?: boolean;
  hide?: boolean;
  onClick: () => void;
} & ComponentBaseProps;

type ProjectFileDiffBarProps = {
  openFile: {
    filename: string;
    creator: User;
    fileLastModified: string;
    manager: {
      profile: string;
      basepath: string;
      file: {
        creator: { username: string };
        lastUpdated: string;
        fileLastModified: string;
      };
    };
  };
  username?: string;
  loggedInUsername?: string;
  isBaseFile?: boolean;
  isCloudDiffFile?: boolean;
  cannotView?: boolean;
};

export const SelectedTag = () => {
  return (
    <div className='flex items-center'>
      <p className=' text-wfBlack'>Selected</p>
      <MdOutlineCheckCircle className=' ml-1' color={themes.color.wfGreen} />
    </div>
  );
};

export const ConflictButton = ({
  text,
  icon,
  onClick,
  isSelected,
  hide,
  className = '',
}: ConflictButtonProps) => {
  if (hide) {
    return null;
  }

  return (
    <div className='fixed bottom-8 w-1/2'>
      <div
        className={
          className || ' sticky flex items-center justify-center z-[9999]'
        }
      >
        {isSelected ? (
          <SelectedTag />
        ) : (
          <Button
            outline
            text={text || 'Select'}
            style={{
              pointerEvents: 'auto',
            }}
            icon={icon}
            size='small'
            iconPosition='right'
            onClick={onClick}
          />
        )}
      </div>
    </div>
  );
};

type OpenCADRoomsProps = {
  name: string;
  filename?: string;
  params: Record<string, any>;
  style?: React.CSSProperties;
};

export const OpenCADRooms = ({
  style = {},
  name,
  filename,
  params,
}: OpenCADRoomsProps) => {
  if (!name) return null;

  return (
    <div
      className='flex items-center justify-center sticky bg-transparent hover:bg-white border-wfGrey100 z-[9999]'
      data-testid='CADPage-OpenCADRooms'
      style={{
        height: CADComparisonHeight,
        width: `calc(100% - ${NavCubWidth}px)`,
        zIndex: 2,
        ...style,
      }}
    >
      <Link
        name={name}
        params={params}
        openTab={{
          title: filename,
          iconType: 'file',
        }}
        style={{ pointerEvents: 'all' }}
        target={isDesktopMode ? null : '_blank'}
      >
        <p
          data-for='diffToolTip'
          data-position='right'
          className='flex items-center mx-2 ml-4 text-wfBlue'
        >
          <CadFileIcon />
          <div className=' mx-2'>Open in CAD Rooms</div>
          <Icon name='arrow_right' />
        </p>
      </Link>
    </div>
  );
};

export const ProjectFileDiffBar = ({
  openFile,
  isBaseFile,
  isCloudDiffFile,
  username,
}: ProjectFileDiffBarProps) => {
  if (!openFile) return null;

  const { filename, manager } = openFile;
  const { file } = manager || {};

  const { fileLastModified } = file || {};

  let title = '';
  if (isDesktopMode) {
    if (isBaseFile) title = 'Cloud File';
    else if (isCloudDiffFile) title = 'Cloud File (previous)';
    else title = 'Local File';
  } else {
    title = `(${isBaseFile ? 'Base' : 'Edit'})`;
  }

  return (
    <>
      <div
        className='flex items-center justify-between sticky border-b w-full bg-wfWhite border-wfGrey100 z-[9999] pointer-events-auto'
        data-testid='CADPage-OpenDiffFile'
        style={{ height: CADComparisonHeight }}
      >
        <div className='flex items-center mx-2 ml-4 text-wfBlack'>
          {isDesktopMode && (
            <div className=' mr-2'>
              {isBaseFile ? <CloudIcon /> : <NavHomeIcon />}
            </div>
          )}
          {!isDesktopMode && filename} {title}
        </div>
        {isBaseFile && (
          <div className=' mr-4  flex flex-col text-sm text-wfGrey800'>
            <CreateBy username={username} />
            {!!fileLastModified &&
              formatDate(new Date(fileLastModified), 'HH:mm dd/MM/yyyy')}
          </div>
        )}
      </div>
    </>
  );
};

const RevisionsDropdown = ({
  options,
  iconColor,
  titleStyle,
  wrapperStyle,
  selectedRevision,
  selectedKeys,
  onClickOptionItem,
}: any) => {
  const { ref, isOpen, setOpen } = useOutisdeClick(false);

  return (
    <div ref={ref}>
      <DropdownInitialWrapper
        style={wrapperStyle}
        onClick={() => setOpen(!isOpen)}
      >
        <RowTitleWrapper style={titleStyle}>
          {selectedRevision?.title}
        </RowTitleWrapper>
        <Icon
          colorPath={iconColor}
          name={isOpen ? 'caret_up' : 'caret_down'}
          size='small'
        />
      </DropdownInitialWrapper>
      {isOpen && (
        <DropdownContentWrapper className=' mt-1 overflow-y-scroll w-[108px]  bg-white shadow'>
          {options.map((obj: any) => {
            const disabled = selectedKeys?.includes(obj?.version);
            return (
              <div
                onClick={
                  disabled
                    ? null
                    : () => {
                        onClickOptionItem(obj);
                        setOpen(!isOpen);
                      }
                }
                key={obj.id}
                className={`${
                  disabled ? ' cursor-not-allowed' : 'cursor-pointer'
                } text-wfBlack pl-4 py-1  my-1 text-sm hover:bg-wfGrey200 select-none`}
              >
                {obj.title}
              </div>
            );
          })}
        </DropdownContentWrapper>
      )}
    </div>
  );
};

type ShareFileDiffBarProps = {
  // currentVersionNo: number;
  options: SharedFileRevision[];
  diffVersions: string[];
  activeRevision: SharedFileRevision;
  onClickOptionItem: (item: SharedFileRevision) => void;
} & ProjectFileDiffBarProps;

export const ShareFileDiffBar = ({
  openFile,
  options,
  activeRevision,
  onClickOptionItem,
  diffVersions,
}: ShareFileDiffBarProps) => {
  if (!openFile) return null;

  const { fileLastModified, creator } = openFile;
  const { username } = creator || {};
  const { sharedFileManager } = frameStore;
  const revisionsConnection = sharedFileManager?.revisionsConn;
  const [versionStr = '1'] = activeRevision?.title?.match(/\d+/gi) || [];

  const gap = 8;

  return (
    <>
      <div
        className='flex items-center justify-between sticky border-b w-full bg-wfWhite border-wfGrey100 z-[9999] env'
        data-testid='CADPage-ShareDiffFile'
        style={{ height: CADComparisonHeight, pointerEvents: 'all' }}
      >
        <div className='w-full h-full flex items-center'>
          <div
            className=' text-wfBlack border border-wfBlack flex-center mx-2'
            style={{
              minWidth: CADComparisonHeight - gap,
              minHeight: CADComparisonHeight - gap,
            }}
          >{`V${versionStr}`}</div>
          <div className=' flex flex-col text-wfGrey700 text-sm'>
            <CreateBy username={username} className=' flex items-center mr-2' />
            {!!fileLastModified &&
              formatDate(new Date(fileLastModified), 'HH:mm dd/MM/yyyy')}
          </div>
        </div>

        {!!options?.length && (
          <RevisionsDropdown
            iconColor={themes.color.wfBlack}
            titleStyle={{
              color: themes.color.wfBlack,
            }}
            showDateAndUser={false}
            contentStyle={{
              maxWidth: 'unset',
            }}
            options={options}
            onClickOptionItem={onClickOptionItem}
            revisionsConnection={revisionsConnection}
            selectedRevision={activeRevision}
            selectedKeys={diffVersions}
          />
        )}
      </div>
    </>
  );
};
